import { trimmedStringFromUnknown } from "../protocol/shared.js";
import { messageEnvelopeSchema } from "../protocol/message-envelope.js";
import { spaceThreadEventSchema, spaceThreadSchema, spaceThreadSignalPlacementSchema } from "../space-mesh/space-thread.js";
import { signalTextSchema } from "../protocol/signal.js";
import { signalEnvelopeSchema, signalTextEnvelopeSchema } from "../protocol/envelope.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/space-threads.ts
const clientMutationIdSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(200));
const clientMessageIdSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(200));
const decimalIntegerPattern = /^\d+$/u;
const optionalThreadSignalAnchorSchema = z$1.preprocess((value) => value === void 0 || value === null ? void 0 : trimmedStringFromUnknown(value), z$1.string().min(1).optional());
const optionalThreadSignalLimitSchema = z$1.preprocess((value) => {
	if (value === void 0 || value === null) return;
	if (typeof value === "number") return value;
	const normalized = trimmedStringFromUnknown(value);
	return decimalIntegerPattern.test(normalized) ? Number.parseInt(normalized, 10) : NaN;
}, z$1.number().int().min(1).max(100).optional());
const listSpaceThreadSignalsQuerySchema = z$1.strictObject({
	includeWakeContinuation: z$1.preprocess((value) => value === "true" ? true : value, z$1.union([z$1.literal(true), z$1.literal("conversation")]).optional()),
	afterSignalId: optionalThreadSignalAnchorSchema,
	limit: optionalThreadSignalLimitSchema
}).superRefine((value, ctx) => {
	if (value.afterSignalId === void 0 === (value.limit === void 0)) return;
	ctx.addIssue({
		code: "custom",
		message: "afterSignalId and an integer limit from 1 to 100 are required together"
	});
});
const createSpaceThreadBodySchema = z$1.strictObject({
	rootSignalId: z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1)),
	clientMutationId: clientMutationIdSchema
});
const createSpaceThreadResponseSchema = z$1.strictObject({
	thread: spaceThreadSchema,
	rootSignal: signalEnvelopeSchema,
	placement: spaceThreadSignalPlacementSchema
});
const getSpaceThreadResponseSchema = z$1.strictObject({
	thread: spaceThreadSchema,
	rootSignal: z$1.union([signalEnvelopeSchema, z$1.null()])
});
const listSpaceThreadEventsResponseSchema = z$1.strictObject({ events: z$1.array(spaceThreadEventSchema) });
const spaceThreadPreviewSchema = z$1.strictObject({
	threadId: spaceThreadSchema.shape.threadId,
	replyCount: z$1.number().int().min(0),
	signals: z$1.array(signalEnvelopeSchema),
	placements: z$1.array(spaceThreadSignalPlacementSchema)
});
const listSpaceThreadsResponseSchema = z$1.strictObject({
	threads: z$1.array(spaceThreadSchema),
	previews: z$1.array(spaceThreadPreviewSchema).default([])
});
const listSpaceThreadSignalsResponseSchema = z$1.strictObject({
	wakeContinuation: z$1.strictObject({
		targetProfileId: z$1.string().min(1).nullable(),
		automaticTargetProfileId: z$1.string().min(1).nullable().optional()
	}).optional(),
	signals: z$1.array(signalEnvelopeSchema),
	placements: z$1.array(spaceThreadSignalPlacementSchema)
});
const postSpaceThreadSignalBodySchema = z$1.strictObject({
	workHandoff: signalTextSchema.shape.data.shape.meta.unwrap().shape.workHandoff,
	threadWakeContinuation: z$1.literal(true).optional(),
	text: signalTextSchema.shape.data.shape.text,
	deviceWait: signalTextSchema.shape.data.shape.deviceWait.optional(),
	contextSelection: signalTextSchema.shape.data.shape.contextSelection.optional(),
	files: signalTextSchema.shape.data.shape.files.optional(),
	messageEnvelope: messageEnvelopeSchema.optional(),
	clientMessageId: clientMessageIdSchema,
	authorHasRead: signalTextSchema.shape.data.shape.meta.unwrap().shape.authorHasRead,
	restoredFromSignalId: signalTextSchema.shape.data.shape.meta.unwrap().shape.restoredFromSignalId
}).superRefine((value, ctx) => {
	const parsed = signalTextSchema.shape.data.safeParse({
		text: value.text,
		...value.files ? { files: value.files } : {},
		...value.messageEnvelope ? { messageEnvelope: value.messageEnvelope } : {},
		meta: { clientMessageId: value.clientMessageId }
	});
	if (parsed.success) return;
	const firstIssue = parsed.error.issues[0];
	ctx.addIssue({
		code: "custom",
		path: firstIssue?.path ?? [],
		message: firstIssue?.message ?? "Invalid thread signal payload"
	});
});
const postSpaceThreadSignalResponseSchema = z$1.strictObject({
	signal: signalTextEnvelopeSchema,
	placement: spaceThreadSignalPlacementSchema
});
//#endregion
export { createSpaceThreadBodySchema, createSpaceThreadResponseSchema, getSpaceThreadResponseSchema, listSpaceThreadEventsResponseSchema, listSpaceThreadSignalsQuerySchema, listSpaceThreadSignalsResponseSchema, listSpaceThreadsResponseSchema, postSpaceThreadSignalBodySchema, postSpaceThreadSignalResponseSchema, spaceThreadPreviewSchema };

//# sourceMappingURL=space-threads.js.map